import { PDFDocument, StandardFonts, rgb, type PDFFont, type PDFPage } from "pdf-lib";
import type {
  TemplateFormPdfModel,
  TemplateFormPdfRow,
  TemplateFormPdfSignatureRow,
  TemplateFormPdfValueRow,
  TemplateFormPdfWeatherRow,
} from "./templateFormPdf";

/** A4 in points, with the plain black rules the official forms are printed with. */
const PAGE_WIDTH = 595.28;
const PAGE_HEIGHT = 841.89;
const MARGIN = 42;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;
/** Column proportions taken from the official forms: label ~31% of the table,
 *  the number/date column ~29%, the title spanning the ~40% between them. */
const LABEL_WIDTH = Math.round(CONTENT_WIDTH * 0.31);
const HEADER_RIGHT_WIDTH = Math.round(CONTENT_WIDTH * 0.29);
const VALUE_WIDTH = CONTENT_WIDTH - LABEL_WIDTH;
const BODY_SIZE = 10;
/** The weather row's option names, which have the narrowest cells on the form. */
const OPTION_SIZE = 8.5;
const TITLE_SIZE = 13;
const LINE_HEIGHT = 12.5;
const PADDING = 6;
const HEADER_HEIGHT = 62;
const RULE = 0.75;
const BLACK = rgb(0, 0, 0);

/** The WinAnsi codepoints outside Latin-1 that the standard fonts still encode. */
const WIN_ANSI_EXTRAS = "€‚ƒ„…†‡ˆ‰Š‹ŒŽ‘’“”•–—˜™š›œžŸ";

/**
 * Characters WinAnsi cannot encode that carry meaning, so a `?` would lose it.
 * The arrow is ours: the Estonian activity copy separates old and new values
 * with `→` (`{actor} muutis staatuse: {old} → {new}`), which printed as
 * `Töös ? Lahendatud` in the protocol.
 */
const WIN_ANSI_SUBSTITUTES: Record<string, string> = { "→": "->" };

interface Sheet {
  doc: PDFDocument;
  page: PDFPage;
  /** Top of the next thing to draw, in PDF coordinates. */
  y: number;
  font: PDFFont;
  bold: PDFFont;
}

/**
 * The standard fonts encode WinAnsi only, and `drawText` throws on anything
 * else — a pasted emoji would otherwise take the whole PDF down. Newlines
 * stay for the wrapper to split on; a CR (CRLF text from the DB) is dropped
 * rather than turned into a `?`.
 */
export function toWinAnsi(text: string): string {
  return [...text]
    .map((char) => {
      const code = char.codePointAt(0) ?? 0;
      const latin1 = (code >= 0x20 && code <= 0x7e) || (code >= 0xa0 && code <= 0xff);
      if (latin1 || WIN_ANSI_EXTRAS.includes(char)) return char;
      const substitute = WIN_ANSI_SUBSTITUTES[char];
      if (substitute) return substitute;
      if (code === 0x0d) return "";
      return code === 0x0a ? char : "?";
    })
    .join("");
}

function splitWordToWidth(word: string, font: PDFFont, size: number, width: number): string[] {
  const chunks: string[] = [];
  let chunk = "";
  for (const character of word) {
    const candidate = `${chunk}${character}`;
    if (chunk && font.widthOfTextAtSize(candidate, size) > width) {
      chunks.push(chunk);
      chunk = character;
    } else {
      chunk = candidate;
    }
  }
  if (chunk) chunks.push(chunk);
  return chunks;
}

/** Greedy word wrap that also contains unbroken values such as email addresses. */
export function wrapText(text: string, font: PDFFont, size: number, width: number): string[] {
  const lines: string[] = [];
  for (const paragraph of toWinAnsi(text).split("\n")) {
    let line = "";
    for (const word of paragraph.split(" ").filter(Boolean)) {
      if (font.widthOfTextAtSize(word, size) > width) {
        if (line) lines.push(line);
        const chunks = splitWordToWidth(word, font, size, width);
        lines.push(...chunks.slice(0, -1));
        line = chunks.at(-1) ?? "";
        continue;
      }
      const candidate = line ? `${line} ${word}` : word;
      if (line && font.widthOfTextAtSize(candidate, size) > width) {
        lines.push(line);
        line = word;
      } else {
        line = candidate;
      }
    }
    lines.push(line);
  }
  return lines;
}

function addPage(sheet: Sheet) {
  sheet.page = sheet.doc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
  sheet.y = PAGE_HEIGHT - MARGIN;
}

function box(sheet: Sheet, x: number, top: number, width: number, height: number) {
  sheet.page.drawRectangle({
    x,
    y: top - height,
    width,
    height,
    borderWidth: RULE,
    borderColor: BLACK,
  });
}

function text(
  sheet: Sheet,
  value: string,
  x: number,
  baseline: number,
  font: PDFFont = sheet.font,
  size = BODY_SIZE
) {
  sheet.page.drawText(toWinAnsi(value), { x, y: baseline, size, font, color: BLACK });
}

function centred(
  sheet: Sheet,
  value: string,
  left: number,
  width: number,
  baseline: number,
  font: PDFFont = sheet.font,
  size = BODY_SIZE
) {
  const clean = toWinAnsi(value);
  text(
    sheet,
    clean,
    left + (width - font.widthOfTextAtSize(clean, size)) / 2,
    baseline,
    font,
    size
  );
}

function rightAligned(
  sheet: Sheet,
  value: string,
  right: number,
  baseline: number,
  font: PDFFont = sheet.font
) {
  const clean = toWinAnsi(value);
  text(sheet, clean, right - font.widthOfTextAtSize(clean, BODY_SIZE), baseline, font);
}

function drawLines(sheet: Sheet, lines: string[], x: number, top: number, font: PDFFont) {
  lines.forEach((line, index) => {
    text(sheet, line, x, top - PADDING - BODY_SIZE - index * LINE_HEIGHT, font);
  });
}

/**
 * The header block: contractor label and company in the left cell, the
 * document's title centred across the middle, and the number over the date in
 * the right column — the three-cell grid the official forms open with.
 */
function drawHeader(sheet: Sheet, model: TemplateFormPdfModel) {
  const top = sheet.y;
  const middleLeft = MARGIN + LABEL_WIDTH;
  const rightLeft = MARGIN + CONTENT_WIDTH - HEADER_RIGHT_WIDTH;
  const half = HEADER_HEIGHT / 2;

  box(sheet, MARGIN, top, CONTENT_WIDTH, HEADER_HEIGHT);
  for (const x of [middleLeft, rightLeft]) {
    sheet.page.drawLine({
      start: { x, y: top },
      end: { x, y: top - HEADER_HEIGHT },
      thickness: RULE,
      color: BLACK,
    });
  }
  // Only the right column is split in two — the title spans the full height.
  sheet.page.drawLine({
    start: { x: rightLeft, y: top - half },
    end: { x: MARGIN + CONTENT_WIDTH, y: top - half },
    thickness: RULE,
    color: BLACK,
  });

  text(sheet, model.contractorLabel, MARGIN + PADDING, top - PADDING - BODY_SIZE, sheet.bold);
  centred(
    sheet,
    model.organisation,
    MARGIN,
    LABEL_WIDTH,
    top - HEADER_HEIGHT + PADDING + 4,
    sheet.bold
  );
  centred(
    sheet,
    model.title,
    middleLeft,
    rightLeft - middleLeft,
    top - half - TITLE_SIZE / 2 + 2,
    sheet.bold,
    TITLE_SIZE
  );

  const numberBaseline = top - PADDING - BODY_SIZE;
  text(sheet, model.numberLabel, rightLeft + PADDING, numberBaseline, sheet.bold);
  rightAligned(
    sheet,
    model.documentNumber,
    MARGIN + CONTENT_WIDTH - PADDING,
    numberBaseline,
    sheet.bold
  );

  const dateBaseline = top - half - PADDING - BODY_SIZE + 2;
  text(sheet, model.dateLabel, rightLeft + PADDING, dateBaseline, sheet.bold);
  rightAligned(sheet, model.date, MARGIN + CONTENT_WIDTH - PADDING, dateBaseline);

  sheet.y = top - HEADER_HEIGHT;
}

function rowLabelLines(sheet: Sheet, number: number | undefined, label: string): string[] {
  const prefix = number === undefined ? label : `${number}. ${label}`;
  return wrapText(prefix, sheet.bold, BODY_SIZE, LABEL_WIDTH - PADDING * 2);
}

/**
 * One label/value row, or as much of it as fits: a long description would
 * otherwise run off the bottom, so the cell continues on the next page.
 */
function drawValueRow(sheet: Sheet, row: TemplateFormPdfValueRow) {
  let labelLines = rowLabelLines(sheet, row.number, row.label);
  let valueLines = wrapText(row.value, sheet.font, BODY_SIZE, VALUE_WIDTH - PADDING * 2);

  do {
    const fits = Math.floor((sheet.y - MARGIN - PADDING * 2) / LINE_HEIGHT);
    if (fits < 1) {
      // A fresh page always fits several lines, so this cannot loop forever.
      addPage(sheet);
      continue;
    }
    const take = Math.min(Math.max(labelLines.length, valueLines.length), fits);
    drawRowChunk(sheet, labelLines.slice(0, take), valueLines.slice(0, take));
    labelLines = labelLines.slice(take);
    valueLines = valueLines.slice(take);
  } while (labelLines.length > 0 || valueLines.length > 0);
}

function drawRowChunk(sheet: Sheet, labelLines: string[], valueLines: string[]) {
  const height = Math.max(labelLines.length, valueLines.length, 1) * LINE_HEIGHT + PADDING * 2;
  const top = sheet.y;
  box(sheet, MARGIN, top, CONTENT_WIDTH, height);
  sheet.page.drawLine({
    start: { x: MARGIN + LABEL_WIDTH, y: top },
    end: { x: MARGIN + LABEL_WIDTH, y: top - height },
    thickness: RULE,
    color: BLACK,
  });
  drawLines(sheet, labelLines, MARGIN + PADDING, top, sheet.bold);
  drawLines(sheet, valueLines, MARGIN + LABEL_WIDTH + PADDING, top, sheet.font);
  sheet.y = top - height;
}

/**
 * The diary's weather row: time and temperature down the left of a nested
 * grid, the weather options across it. A ticked option is marked in the cell
 * beneath its name, so the printed form shows what was observed.
 */
function drawWeatherRow(sheet: Sheet, row: TemplateFormPdfWeatherRow) {
  const labelLines = rowLabelLines(sheet, row.number, row.label);
  const cellHeight = LINE_HEIGHT + PADDING;
  const height = Math.max(cellHeight * 2, labelLines.length * LINE_HEIGHT + PADDING * 2);
  ensureSpace(sheet, height);

  const top = sheet.y;
  const gridLeft = MARGIN + LABEL_WIDTH;
  const gridWidth = CONTENT_WIDTH - LABEL_WIDTH;
  // Wide enough for the longest left-hand label ("Kellaaeg") at body size.
  const leadWidth = 52;
  const valueWidth = 44;
  const optionWidth = (gridWidth - leadWidth - valueWidth) / row.options.length;

  box(sheet, MARGIN, top, CONTENT_WIDTH, height);
  drawLines(sheet, labelLines, MARGIN + PADDING, top, sheet.bold);

  const columnEdges = [gridLeft, gridLeft + leadWidth, gridLeft + leadWidth + valueWidth];
  for (let i = 0; i < row.options.length; i++) {
    columnEdges.push(gridLeft + leadWidth + valueWidth + optionWidth * (i + 1));
  }
  for (const x of columnEdges) {
    sheet.page.drawLine({
      start: { x, y: top },
      end: { x, y: top - height },
      thickness: RULE,
      color: BLACK,
    });
  }
  sheet.page.drawLine({
    start: { x: gridLeft, y: top - cellHeight },
    end: { x: MARGIN + CONTENT_WIDTH, y: top - cellHeight },
    thickness: RULE,
    color: BLACK,
  });

  const firstBaseline = top - PADDING - BODY_SIZE + 2;
  const secondBaseline = top - cellHeight - PADDING - BODY_SIZE + 2;
  text(sheet, row.timeLabel, gridLeft + PADDING, firstBaseline);
  text(sheet, row.time, columnEdges[1] + PADDING, firstBaseline);
  text(sheet, row.temperatureLabel, gridLeft + PADDING, secondBaseline);
  text(sheet, row.temperature, columnEdges[1] + PADDING, secondBaseline);

  row.options.forEach((option, index) => {
    const left = columnEdges[2] + optionWidth * index;
    // A step down from the body size: the longest option name ("Tugev tuul")
    // has to sit inside its column without being clipped.
    centred(sheet, option.label, left, optionWidth, firstBaseline, sheet.font, OPTION_SIZE);
    if (option.ticked) centred(sheet, "X", left, optionWidth, secondBaseline, sheet.bold);
  });

  sheet.y = top - height;
}

/**
 * A signature block: the statutory label on the left, and on the right a
 * sub-table headed "name, company / certificate" with one line per signatory
 * and the digital-signing marker beneath.
 */
function drawSignatureRow(sheet: Sheet, row: TemplateFormPdfSignatureRow) {
  const labelLines = rowLabelLines(sheet, row.number, row.label);
  const cellHeight = LINE_HEIGHT + PADDING;
  // Header, then a line per signatory plus its marker; a block with nobody in
  // it still prints one empty line, exactly as the blank form does.
  const entries = row.signatories.length > 0 ? row.signatories : [null];
  const marks = (entry: (typeof entries)[number]) =>
    entry ? entry.digitallySigned : row.marksDigitalSignature;
  const bodyLines = entries.reduce<number>((total, entry) => total + (marks(entry) ? 2 : 1), 0);
  const height = Math.max(
    cellHeight * (1 + bodyLines),
    labelLines.length * LINE_HEIGHT + PADDING * 2
  );
  ensureSpace(sheet, height);

  const top = sheet.y;
  const gridLeft = MARGIN + LABEL_WIDTH;
  const splitX = gridLeft + (CONTENT_WIDTH - LABEL_WIDTH) * 0.62;

  box(sheet, MARGIN, top, CONTENT_WIDTH, height);
  drawLines(sheet, labelLines, MARGIN + PADDING, top, sheet.bold);
  for (const x of [gridLeft, splitX]) {
    sheet.page.drawLine({
      start: { x, y: top },
      end: { x, y: top - height },
      thickness: RULE,
      color: BLACK,
    });
  }

  const columnWidth = splitX - gridLeft;
  const certificateWidth = MARGIN + CONTENT_WIDTH - splitX;
  centred(
    sheet,
    row.columns.nameAndCompany,
    gridLeft,
    columnWidth,
    top - PADDING - BODY_SIZE + 2,
    sheet.bold
  );
  centred(
    sheet,
    row.columns.certificate,
    splitX,
    certificateWidth,
    top - PADDING - BODY_SIZE + 2,
    sheet.bold
  );

  let lineTop = top - cellHeight;
  sheet.page.drawLine({
    start: { x: gridLeft, y: lineTop },
    end: { x: MARGIN + CONTENT_WIDTH, y: lineTop },
    thickness: RULE,
    color: BLACK,
  });

  for (const entry of entries) {
    const baseline = lineTop - PADDING - BODY_SIZE + 2;
    if (entry) {
      centred(sheet, entry.nameAndCompany, gridLeft, columnWidth, baseline);
      centred(sheet, entry.certificate, splitX, certificateWidth, baseline);
    }
    lineTop -= cellHeight;
    if (marks(entry)) {
      centred(
        sheet,
        row.digitallySignedMarker,
        gridLeft,
        columnWidth,
        lineTop - PADDING - BODY_SIZE + 2
      );
      lineTop -= cellHeight;
    }
  }

  sheet.y = top - height;
}

/** Starts a new page when the next block would not fit on this one. */
function ensureSpace(sheet: Sheet, height: number) {
  if (sheet.y - height < MARGIN) addPage(sheet);
}

function drawRow(sheet: Sheet, row: TemplateFormPdfRow) {
  if (row.kind === "weather") return drawWeatherRow(sheet, row);
  if (row.kind === "signatures") return drawSignatureRow(sheet, row);
  return drawValueRow(sheet, row);
}

/** Franklin's own fields, below the statutory table and clearly its own block. */
function drawAdditional(sheet: Sheet, model: TemplateFormPdfModel) {
  if (model.additionalRows.length === 0) return;
  ensureSpace(sheet, LINE_HEIGHT * 3);
  sheet.y -= LINE_HEIGHT * 1.5;
  text(sheet, model.additionalHeading, MARGIN, sheet.y, sheet.bold);
  sheet.y -= PADDING;
  for (const row of model.additionalRows) drawValueRow(sheet, row);
}

/** Draws the model as an A4 PDF and hands back its bytes. */
export async function renderTemplateFormPdf(model: TemplateFormPdfModel): Promise<Uint8Array> {
  const doc = await PDFDocument.create();
  const sheet: Sheet = {
    doc,
    page: doc.addPage([PAGE_WIDTH, PAGE_HEIGHT]),
    y: PAGE_HEIGHT - MARGIN,
    font: await doc.embedFont(StandardFonts.Helvetica),
    bold: await doc.embedFont(StandardFonts.HelveticaBold),
  };
  doc.setTitle(toWinAnsi(`${model.title} ${model.documentNumber}`));

  drawHeader(sheet, model);
  for (const row of model.rows) drawRow(sheet, row);
  drawAdditional(sheet, model);

  return doc.save();
}
